import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

// The demo client for one tour run. Module scope, never persisted: demo mode must
// not survive a reload, which is what makes a mid-tour refresh land safely on real
// data.
//
// There is deliberately NO lifecycle state machine here. Which page renders demo
// data is derived by the boundary from the committed location and the run's demo
// routes; this module only owns "does a client exist yet, and did it fail". An
// earlier design mutated route/phase state from the tour transitions and produced
// a bug at every seam — the route was retired before navigation committed, a
// teardown raced a pending build, a failure cleared the state that was supposed to
// render the fallback. None of those states exist any more.

export const DEMO_LABEL = 'Demo data — not your store';

export type DemoStartOutcome = 'ready' | 'failed' | 'superseded';

let client: ApolloClient<NormalizedCacheObject> | null = null;
let failed = false;
// Bumped on dispose so a build still in flight cannot install itself afterwards.
let generation = 0;
let pending: Promise<DemoStartOutcome> | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

export function subscribeDemoSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getDemoClient(): ApolloClient<NormalizedCacheObject> | null {
  return client;
}

/** True once the fixtures failed for this run; demo steps then show their illustration. */
export function hasDemoFailed(): boolean {
  return failed;
}

/** Snapshot for useSyncExternalStore — identity changes only when something did. */
export function getDemoSnapshot(): ApolloClient<NormalizedCacheObject> | null {
  return client;
}

export function disposeDemoSession(): void {
  generation += 1;
  pending = null;
  client = null;
  failed = false;
  emit();
}

function fail(error: Error): void {
  console.warn('[tour demo] falling back to the illustrated tour:', error.message);
  failed = true;
  client = null;
  emit();
}

/**
 * Build the demo client if this run does not have one yet. Fixtures are imported
 * lazily so a merchant who never opens the tour never downloads them.
 *
 * `failed` and `superseded` are distinct on purpose: superseded means the run was
 * disposed while this build was in flight and the caller must abandon its
 * transition; failed means the step simply is not demo-backed and the caller must
 * still ADVANCE, because the failure flag is what selects the illustrated
 * fallback. Collapsing them strands the tour on the previous step.
 */
export function ensureDemoClient(): Promise<DemoStartOutcome> {
  if (client) return Promise.resolve('ready');
  if (failed) return Promise.resolve('failed');
  if (pending) return pending;

  const startedAt = generation;

  const build = (async (): Promise<DemoStartOutcome> => {
    try {
      const [{ buildDemoScenario }, { createDemoClient }] = await Promise.all([
        import('./demo-scenario'),
        import('./demo-client'),
      ]);

      if (startedAt !== generation) return 'superseded';

      const scenario = buildDemoScenario(Date.now());
      client = createDemoClient(scenario, (error) => {
        if (startedAt === generation) fail(error);
      });
      emit();
      return 'ready';
    } catch (caught) {
      if (startedAt !== generation) return 'superseded';
      fail(caught instanceof Error ? caught : new Error(String(caught)));
      return 'failed';
    } finally {
      if (startedAt === generation) pending = null;
    }
  })();

  pending = build;
  return build;
}
